<template>
  <Content
    class="route-plan-list"
    :header-label="t('routeBuilder.routePlans', 2)"
    header-icon="map-marked-alt"
    full-width
  >
    <template #header>
      <div
        class="content-header flex justify-content-between align-items-center"
      >
        <div class="flex flex-gap-1 align-items-center">
          <h2 class="m-0">{{ t('routeBuilder.routePlans', 2) }}</h2>
          <LoadingIndicator :loading="results.loading" />
        </div>
        <RygenButton
          v-if="canOpen"
          mode="primary"
          :icon="PrimeIcons.PLUS"
          :label="t('routeBuilder.newRoutePlan')"
          @click="newRoutePlan"
        />
      </div>
    </template>

    <DataTable
      :value="results.data"
      :loading="results.loading"
      data-key="id"
      responsive-layout="scroll"
      removable-sort
      scrollable
      scroll-height="flex"
      :pt="{ table: { 'aria-label': t('routeBuilder.routePlans', 2) } }"
    >
      <template #empty>
        {{ t('common.noParamYet', { param: t('routeBuilder.routePlans', 2) }) }}
      </template>

      <Column field="status" :header="t('common.status')" sortable>
        <template #body="{ data }: { data: RoutePlanSummary }">
          <RygenChip
            :label="statusLabel(data)"
            :severity="statusSeverity(data)"
          />
        </template>
      </Column>

      <Column
        field="shipmentCount"
        :header="t('shipments.shipments', 2)"
        sortable
      />

      <Column :header="t('orders.origin')">
        <template #body="{ data }: { data: RoutePlanSummary }">
          {{ data.originSummary ?? '—' }}
        </template>
      </Column>

      <Column :header="t('orders.destination')">
        <template #body="{ data }: { data: RoutePlanSummary }">
          {{ data.destSummary ?? '—' }}
        </template>
      </Column>

      <Column field="createdBy" :header="t('common.createdBy')" sortable>
        <template #body="{ data }: { data: RoutePlanSummary }">
          {{ data.createdBy ?? '—' }}
        </template>
      </Column>

      <Column field="createdDate" :header="t('common.created')" sortable>
        <template #body="{ data }: { data: RoutePlanSummary }">
          {{ formatDate(data.createdDate) }}
        </template>
      </Column>

      <Column :header-style="{ width: '7rem' }" align-frozen="right" frozen>
        <template #body="{ data }: { data: RoutePlanSummary }">
          <span class="flex flex-gap-1 justify-content-end">
            <RygenButton
              v-if="canOpen && isOpenable(data)"
              mode="tertiary"
              :icon="PrimeIcons.PENCIL"
              :aria-label="t('common.open')"
              @click="openPlan(data)"
            />
            <RygenButton
              v-if="canDelete && isDeletable(data)"
              mode="tertiary"
              :icon="PrimeIcons.TRASH"
              :severity="Severity.DANGER"
              :aria-label="t('common.delete')"
              @click="confirmDelete(data)"
            />
          </span>
        </template>
      </Column>
    </DataTable>
  </Content>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { useFlag } from '@unleash/proxy-client-vue';
import type { Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import authorization from '@rygen/rygen-ui/authorization';
import { useDeleteConfirmation, useToast } from '@rygen/rygen-ui/composables';
import { translateEnum } from '@rygen/rygen-ui/i18n';
import { Loadable, Severity } from '@rygen/rygen-ui/model';

import api from '@/api';
import { RoutePlanStatus, RoutePlanSummary } from '@/model/routeBuilder/phase2';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import LoadingIndicator from '@rygen/rygen-ui/components/ui/LoadingIndicator.vue';
import RygenChip from '@rygen/rygen-ui/components/ui/RygenChip.vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';

const { requestDeleteConfirmation } = useDeleteConfirmation();
const router = useRouter();
const { t } = useI18n();
const toast = useToast();

const isTransportSegmentEnabled = useFlag(
  'route-builder-transport-segment-feat-flag',
);

const canDelete = computed(() =>
  authorization.hasAuthority('shipment', 'consolidate'),
);
const canOpen = computed(() =>
  authorization.hasAuthority('shipment', 'consolidate'),
);

const results: Ref<Loadable<RoutePlanSummary[]>> = ref({
  loading: false,
  data: [],
});

// A DRAFT that already has committed shipments is a "committed" plan — distinct
// from an empty draft — even though the backend keeps status DRAFT until the plan
// freezes to EXECUTED on first pickup. Surface that as its own list label/severity.
const isCommitted = (plan: RoutePlanSummary) =>
  plan.status === RoutePlanStatus.DRAFT && plan.shipmentCount > 0;

const statusLabel = (plan: RoutePlanSummary) =>
  isCommitted(plan)
    ? t('routeBuilder.committed')
    : translateEnum(plan.status, 'RoutePlanStatus');

const statusSeverity = (plan: RoutePlanSummary) => {
  if (plan.status === RoutePlanStatus.EXECUTED) {
    return Severity.SUCCESS;
  }
  return isCommitted(plan) ? Severity.INFO : undefined;
};

// Default list order: draft → committed → executed. Committed is a DRAFT with
// shipments, so the backend status alone can't express it — rank on the FE.
const statusRank = (plan: RoutePlanSummary): number => {
  if (plan.status === RoutePlanStatus.EXECUTED) {
    return 2;
  }
  return isCommitted(plan) ? 1 : 0;
};

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString() : '—';

const isDeletable = (plan: RoutePlanSummary) =>
  plan.status === RoutePlanStatus.DRAFT && plan.shipmentCount === 0;

// Only a pure DRAFT (no committed shipments) opens into the builder this round.
// Opening a committed DRAFT or an EXECUTED plan — even read-only to view it — is
// deferred to a later round; a committed DRAFT is also locked from editing until
// it freezes on first pickup (re-editing would fork from shipments that exist).
const isOpenable = (plan: RoutePlanSummary) =>
  plan.status === RoutePlanStatus.DRAFT && plan.shipmentCount === 0;

const loadRoutePlans = async () => {
  try {
    results.value.loading = true;
    // Default order: draft → committed → executed, then newest first within each.
    // (removable-sort reverts to this order; the user can still sort by any column.)
    results.value.data = (await api.routePlan.findRoutePlans()).sort(
      (a, b) =>
        statusRank(a) - statusRank(b) ||
        (b.createdDate ?? '').localeCompare(a.createdDate ?? ''),
    );
  } catch (e) {
    results.value.error = e;
    console.error('Error loading route plans', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('routeBuilder.routePlans', 2),
      }),
      error: e,
    });
  } finally {
    results.value.loading = false;
  }
};

const openPlan = (plan: RoutePlanSummary) => {
  router.push({
    name: 'routeBuilderPhase2',
    query: { routePlanId: plan.id },
  });
};

// Open a fresh, empty builder. The container route redirects to the phase the
// transport-segment flag selects (same target the old view toggle used).
const newRoutePlan = () => {
  router.push({ name: 'routeBuilder' });
};

const confirmDelete = (plan: RoutePlanSummary) => {
  requestDeleteConfirmation({
    param: t('routeBuilder.routePlans', 1),
    accept: () => deletePlan(plan),
  });
};

const deletePlan = async (plan: RoutePlanSummary) => {
  if (plan.id == null) {
    return;
  }
  try {
    results.value.loading = true;
    await api.routePlan.deleteRoutePlan(plan.id);
    toast.addSuccess({
      detail: t('messages.successDeletingParam', {
        param: t('routeBuilder.routePlans'),
      }),
    });
    await loadRoutePlans();
  } catch (e) {
    console.error('Error deleting route plan', e);
    toast.addError({
      detail: t('errors.deletingParam', {
        param: t('routeBuilder.routePlans'),
      }),
      error: e,
    });
    results.value.loading = false;
  }
};

// Load once the transport-segment flag resolves on. It hydrates to false and,
// being domain-scoped, can settle after flagsReady — so DON'T redirect on a
// false reading (that bounced users to the builder even when the flag was on).
// A genuinely flag-off visitor just sees an empty list (the backend gates it too).
watch(
  isTransportSegmentEnabled,
  (enabled) => {
    if (enabled) {
      loadRoutePlans();
    }
  },
  { immediate: true },
);
</script>

<style>
.route-plan-list {
  .p-datatable {
    height: 100%;
  }
}
</style>
